import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import CustomPagination from '../custom-pagination/CustomPagination';
import CustomSelect from '../custom-select/CustomSelect';
import './sim-table.css';

/**
 * SimTable — таблица SIM-карт
 * По макету Figma 26-04 wa Proxy Manager (node 383-69904)
 * 
 * Столбцы:
 * Checkbox, Модем, SIM, ГЕО, Сервер, Номер модема, Номер, Оператор,
 * Сумма ₽, Последний платеж, Следующий платеж, Личный кабинет, Оформлена, Комментарий, Действия
 */

const ALFA_MOBILE_PAY_BASE = 'https://web.alfabank.ru/payments/mobile';

/** Пустой выбор фильтра столбца: одна ссылка, иначе `|| []` на каждом рендере ломает синхронизацию чекбоксов */
const EMPTY_TABLE_FILTER_SELECTION = [];

/** Токен фильтра «без данных» для IMEI/MAC — совпадает с бэкендом sim_cards_api */
const IDENTITY_EMPTY_FILTER = '__EMPTY__';

/** Стабильный ключ для useEffect: одинаковое содержимое при разных ссылках на массив */
const serializeFilterSelection = (values) =>
  JSON.stringify((values || []).map((v) => String(v)).sort());

/** 10 цифр без +7/8 для оплаты мобильной связи в Альфа-Онлайн */
const getRussianMobileDigitsForPayment = (phoneStr) => {
  if (!phoneStr) return '';
  const digits = String(phoneStr).replace(/\D/g, '');
  if (digits.length === 11 && (digits[0] === '7' || digits[0] === '8')) {
    return digits.slice(1);
  }
  if (digits.length === 10) {
    return digits;
  }
  return '';
};

const buildAlfaMobilePaymentUrl = (phoneStr, amount) => {
  const phone = getRussianMobileDigitsForPayment(phoneStr);
  if (!phone || phone.length !== 10) return null;
  const amountInt = Math.max(0, Math.round(Number(amount)) || 0);
  const params = new URLSearchParams({ phone, amount: String(amountInt) });
  return `${ALFA_MOBILE_PAY_BASE}?${params.toString()}`;
};

/**
 * Ссылка ЛК по шаблону из настроек операторов.
 * `{phone}` — номер из привязанного личного кабинета (personal_account.phone_number).
 * `{phone_number}` — номер SIM из таблицы (поле номера телефона строки).
 * Если в шаблоне есть плейсхолдер, а значение пустое — ссылка не строится (кнопка «ЛК» неактивна).
 */
const buildPersonalCabinetHrefFromTemplate = (urlTemplate, { phone: phoneRaw, phone_number: phoneNumberRaw }) => {
  const template = String(urlTemplate || '').trim();
  if (!template) return null;
  const phone = phoneRaw != null ? String(phoneRaw).trim() : '';
  const phoneNumber = phoneNumberRaw != null ? String(phoneNumberRaw).trim() : '';
  if (template.includes('{phone}') && !phone) return null;
  if (template.includes('{phone_number}') && !phoneNumber) return null;
  return template
    .replace(/\{phone_number\}/g, phoneNumber)
    .replace(/\{phone\}/g, phone);
};

// ==================== SVG ICONS ====================

// Иконка сортировки (caret-down)
const SortIcon = ({ active = false, direction = 'asc' }) => (
  <svg
    className={`smt__sort-icon ${active ? 'smt__sort-icon--active' : ''} ${direction === 'desc' ? 'smt__sort-icon--desc' : ''}`}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 9L12 15L18 9"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Иконка фильтра
const FilterIcon = ({ active = false }) => (
  <svg
    className={`smt__filter-icon ${active ? 'smt__filter-icon--active' : ''}`}
    width="15"
    height="15"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.00005 14C8.89187 14 8.7866 13.9649 8.70005 13.9L6.70005 12.4C6.63795 12.3534 6.58755 12.293 6.55284 12.2236C6.51812 12.1542 6.50005 12.0776 6.50005 12V9.19L2.99205 5.2435C2.74291 4.96244 2.58024 4.61541 2.52359 4.24412C2.46694 3.87283 2.51872 3.49308 2.67271 3.15051C2.8267 2.80794 3.07635 2.51713 3.39164 2.31303C3.70694 2.10894 4.07446 2.00024 4.45005 2H11.5501C11.9256 2.00044 12.293 2.10932 12.6082 2.31355C12.9234 2.51779 13.1729 2.80869 13.3267 3.15129C13.4805 3.4939 13.5321 3.87364 13.4753 4.24487C13.4185 4.61611 13.2558 4.96305 13.0066 5.244L9.50005 9.19V13.5C9.50005 13.6326 9.44737 13.7598 9.3536 13.8536C9.25984 13.9473 9.13266 14 9.00005 14ZM7.50005 11.75L8.50005 12.5V9C8.50015 8.87758 8.54516 8.75945 8.62655 8.668L12.2606 4.5795C12.3817 4.44254 12.4608 4.27352 12.4883 4.09273C12.5157 3.91193 12.4905 3.72705 12.4154 3.56028C12.3404 3.39351 12.2189 3.25193 12.0654 3.15254C11.9118 3.05316 11.7329 3.00019 11.5501 3H4.45005C4.26728 3.00028 4.08846 3.05327 3.93505 3.15263C3.78164 3.25199 3.66015 3.3935 3.58516 3.56018C3.51016 3.72685 3.48483 3.91163 3.51221 4.09234C3.5396 4.27305 3.61854 4.44203 3.73955 4.579L7.37405 8.668C7.45526 8.75952 7.50009 8.87764 7.50005 9V11.75Z"
      fill="currentColor" stroke="currentColor" strokeWidth="0.6"
    />
  </svg>
);

// Иконка меню (три точки вертикально)
const MenuDotsIcon = () => (
  <svg
    className="smt__menu-icon"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="12" cy="4" r="2" fill="currentColor" />
    <circle cx="12" cy="12" r="2" fill="currentColor" />
    <circle cx="12" cy="20" r="2" fill="currentColor" />
  </svg>
);

// Иконка checkbox (пустой квадрат)
const CheckboxIcon = ({ checked = false, indeterminate = false }) => {
  if (indeterminate) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" width="15" height="15" rx="1.5" stroke="#C2C2C2" fill="white" />
        <rect x="4" y="7" width="8" height="2" rx="1" fill="#5283F4" />
      </svg>
    );
  }
  if (checked) {
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="0.5" y="0.5" width="15" height="15" rx="1.5" fill="#5283F4" stroke="#5283F4" />
        <path d="M4 8L7 11L12 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="0.5" width="15" height="15" rx="1.5" stroke="#C2C2C2" fill="white" />
    </svg>
  );
};

// ==================== BADGE COMPONENTS ====================

// Бейдж статуса модема
const StatusBadge = ({ status }) => {
  const statusLower = (status || '').toLowerCase();
  let badgeClass = 'smt__badge';
  
  if (
    statusLower.includes('отключен')
    || statusLower === 'disabled'
    || statusLower.includes('недоступен')
    || statusLower === 'unavailable'
    || statusLower === 'not_working'
  ) {
    badgeClass += ' smt__badge--red';
  } else if (statusLower.includes('доступен') || statusLower === 'available' || statusLower === 'working') {
    badgeClass += ' smt__badge--green';
  } else if (statusLower.includes('неизвестно') || statusLower === 'unknown') {
    badgeClass += ' smt__badge--yellow';
  }
  
  return <span className={badgeClass}>{status}</span>;
};

// Бейдж SIM статуса (+ опционально красный «!» при длительной недоступности модема ≥72ч)
const SimStatusBadge = ({ status, modemLongUnavailable = false }) => {
  const statusLower = (status || '').toLowerCase();
  let badgeClass = 'smt__badge';

  if (statusLower.includes('работает') || statusLower === 'working') {
    badgeClass += ' smt__badge--blue';
  } else if (statusLower.includes('заморожен') || statusLower === 'frozen') {
    badgeClass += ' smt__badge--frozen';
  } else if (statusLower.includes('свободен') || statusLower === 'free') {
    badgeClass += ' smt__badge--gray';
  } else if (statusLower.includes('резерв спб')) {
    badgeClass += ' smt__badge--cyan';
  } else if (statusLower.includes('резерв мск')) {
    badgeClass += ' smt__badge--purple';
  } else if (statusLower.includes('проверить') || statusLower === 'check') {
    badgeClass += ' smt__badge--yellow';
  } else if (statusLower.includes('заменить') || statusLower === 'replace') {
    badgeClass += ' smt__badge--red';
  }

  return (
    <span className="smt__sim-status-wrap">
      <span className={badgeClass}>{status}</span>
      {modemLongUnavailable ? (
        <span
          className="smt__sim-alert"
          title="Модем недоступен непрерывно более 72 часов (по данным проверок прокси)"
          aria-label="Внимание: модем недоступен более 72 часов"
        >
          !
        </span>
      ) : null}
    </span>
  );
};

// Бейдж оператора
const OperatorBadge = ({ operator }) => {
  const operatorLower = (operator || '').toLowerCase();
  let badgeClass = 'smt__badge smt__badge--operator';
  
  if (operatorLower.includes('мтс') || operatorLower === 'mts') {
    badgeClass += ' smt__badge--mts';
  } else if (operatorLower.includes('мегафон') || operatorLower === 'megafon') {
    badgeClass += ' smt__badge--megafon';
  } else if (operatorLower.includes('tele2') || operatorLower.includes('т2')) {
    badgeClass += ' smt__badge--tele2';
  } else if (operatorLower.includes('билайн') || operatorLower === 'beeline') {
    badgeClass += ' smt__badge--beeline';
  } else if (operatorLower.includes('yota') || operatorLower.includes('йота')) {
    badgeClass += ' smt__badge--yota';
  }
  
  return <span className={badgeClass}>{operator}</span>;
};

// ==================== EDITABLE COLUMNS CONFIG ====================

const EDITABLE_COLUMNS = {
  modem: { type: 'search-select' },
  modemNumber: { type: 'search-select' },
  sim: { type: 'select' },
  geo: { type: 'select' },
  server: { type: 'select' },
  phoneNumber: { type: 'text' },
  operator: { type: 'select' },
  amount: { type: 'number' },
  lastPayment: { type: 'date' },
  nextPayment: { type: 'date' },
  freezeUntil: { type: 'date' },
  registeredBy: { type: 'text' },
  registeredOn: { type: 'text' },
  comment: { type: 'text' },
};

/**
 * Получение raw-значения ячейки для редактирования
 * Возвращает значение в формате, пригодном для input/select
 */
const getEditValue = (row, columnKey) => {
  switch (columnKey) {
    case 'modem': return row._raw?.kraken_server_id != null ? String(row._raw.kraken_server_id) : '';
    case 'modemNumber': return row._raw?.kraken_server_id != null ? String(row._raw.kraken_server_id) : '';
    case 'sim': return row._raw?.sim_status || '';
    case 'operator': return row.operator || '';
    case 'geo': return (row._raw?.geo != null && row._raw.geo !== '') ? String(row._raw.geo) : '';
    case 'server': return row.server || '';
    case 'phoneNumber': return row.phoneNumber || '';
    case 'amount': return row.amount != null ? String(row.amount) : '';
    case 'lastPayment': {
      const d = row._raw?.last_payment_date;
      if (!d) return '';
      return String(d).split('T')[0];
    }
    case 'nextPayment': {
      const d = row._raw?.next_payment_date;
      if (!d) return '';
      return String(d).split('T')[0];
    }
    case 'freezeUntil': {
      const d = row._raw?.freeze_until;
      if (!d) return '';
      return String(d).split('T')[0];
    }
    case 'registeredBy': return row.registeredBy || '';
    case 'registeredOn': return row._raw?.registered_by || row.registeredOn || '';
    case 'comment': return row.comment || '';
    default: return '';
  }
};

// ==================== SEARCH SELECT EDITOR ====================

/**
 * SearchSelectEditor — выпадающий список с поиском
 * Используется для выбора модема (поиск по номеру/имени)
 */
const SearchSelectEditor = ({ value, options = [], onSave, onCancel }) => {
  const [searchText, setSearchText] = useState('');
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const savedRef = useRef(false);
  const [dropdownPos, setDropdownPos] = useState(null);

  // Фокус на input при монтировании + вычисление позиции dropdown
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      const rect = inputRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 220;
      const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
      setDropdownPos({
        left: rect.left,
        ...(openAbove
          ? { bottom: window.innerHeight - rect.top + 2 }
          : { top: rect.bottom + 2 }),
      });
    }
  }, []);

  // Закрытие по клику вне (input + dropdown)
  useEffect(() => {
    const handleClickOutside = (e) => {
      const isInsideInput = inputRef.current && inputRef.current.contains(e.target);
      const isInsideDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
      if (!isInsideInput && !isInsideDropdown) {
        if (!savedRef.current) {
          savedRef.current = true;
          onCancel();
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onCancel]);

  // Обновление позиции при скролле/ресайзе
  useEffect(() => {
    const updatePos = () => {
      if (inputRef.current) {
        const rect = inputRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownHeight = 220;
        const openAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
        setDropdownPos({
          left: rect.left,
          ...(openAbove
            ? { bottom: window.innerHeight - rect.top + 2 }
            : { top: rect.bottom + 2 }),
        });
      }
    };
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, []);

  const filteredOptions = options.filter((opt) => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      String(opt.modemNumber || '').includes(q) ||
      (opt.name || '').toLowerCase().includes(q) ||
      (opt.label || '').toLowerCase().includes(q) ||
      (opt.connectionName || '').toLowerCase().includes(q)
    );
  });

  const handleSelect = (optValue) => {
    if (savedRef.current) return;
    savedRef.current = true;
    onSave(optValue);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (!savedRef.current) {
        savedRef.current = true;
        onCancel();
      }
    } else if (e.key === 'Enter' && filteredOptions.length === 1) {
      e.preventDefault();
      handleSelect(filteredOptions[0].value);
    }
  };

  const currentOption = options.find((opt) => String(opt.value) === String(value));

  const dropdownContent = (
    <div
      ref={dropdownRef}
      className="smt__search-select-dropdown smt__search-select-dropdown--portal"
      style={dropdownPos ? {
        position: 'fixed',
        left: dropdownPos.left,
        ...(dropdownPos.top != null ? { top: dropdownPos.top } : {}),
        ...(dropdownPos.bottom != null ? { bottom: dropdownPos.bottom } : {}),
      } : { display: 'none' }}
    >
      <button
        type="button"
        className={`smt__search-select-option ${!value ? 'smt__search-select-option--active' : ''}`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => handleSelect('')}
      >
        <span className="smt__search-select-name">— Не назначен —</span>
      </button>
      {filteredOptions.map((opt) => (
        <button
          key={opt.value}
          type="button"
          className={`smt__search-select-option ${String(opt.value) === String(value) ? 'smt__search-select-option--active' : ''}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => handleSelect(opt.value)}
        >
          <span className="smt__search-select-number">№{opt.modemNumber}</span>
          <span className="smt__search-select-name">{opt.name}</span>
        </button>
      ))}
      {filteredOptions.length === 0 && searchText && (
        <div className="smt__search-select-empty">Нет совпадений</div>
      )}
    </div>
  );

  return (
    <div className="smt__search-select">
      <input
        ref={inputRef}
        type="text"
        className="smt__search-select-input"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={currentOption ? currentOption.label : 'Поиск модема...'}
        aria-label="Поиск модема"
      />
      {ReactDOM.createPortal(dropdownContent, document.body)}
    </div>
  );
};

// ==================== INLINE EDITOR ====================

/**
 * InlineEditor — компонент инлайн-редактирования ячейки
 * Поддерживает типы: select, text, number, date, search-select
 * Enter/blur — сохранение, Escape — отмена
 */
const InlineEditor = ({ type, value, options = [], onSave, onCancel }) => {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef(null);
  const savedRef = useRef(false);

  useEffect(() => {
    setLocalValue(value);
    savedRef.current = false;
  }, [value]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' || type === 'number') {
        inputRef.current.select();
      }
    }
  }, [type]);

  const handleSave = useCallback((val) => {
    if (savedRef.current) return;
    savedRef.current = true;
    onSave(val);
  }, [onSave]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave(localValue);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (!savedRef.current) {
        savedRef.current = true;
        onCancel();
      }
    }
  };

  const handleBlur = () => {
    handleSave(localValue);
  };

  if (type === 'search-select') {
    return (
      <SearchSelectEditor
        value={value}
        options={options}
        onSave={onSave}
        onCancel={onCancel}
      />
    );
  }

  if (type === 'select') {
    return (
      <CustomSelect
        className="smt__inline-editor-cs"
        value={localValue}
        onChange={(val) => handleSave(val)}
        options={options}
        ariaLabel="Редактирование"
        defaultOpen
        onClose={onCancel}
      />
    );
  }

  if (type === 'date') {
    return (
      <input
        ref={inputRef}
        type="date"
        className="smt__inline-editor smt__inline-editor--date"
        value={localValue}
        onClick={(e) => { try { e.target.showPicker(); } catch (_) { /* fallback */ } }}
        onChange={(e) => setLocalValue(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        aria-label="Редактирование даты"
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type={type}
      className="smt__inline-editor smt__inline-editor--input"
      value={localValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      step={type === 'number' ? '0.01' : undefined}
      aria-label="Редактирование"
    />
  );
};

// ==================== COLUMN FILTER DROPDOWN ====================

const ColumnFilterDropdown = ({
  columnKey,
  title,
  options = [],
  selectedValues = [],
  onApply,
  onClose,
  emptyOption = null,
}) => {
  const selectedKey = useMemo(() => serializeFilterSelection(selectedValues), [selectedValues]);
  const [localSelected, setLocalSelected] = useState(() =>
    (selectedValues || []).map((v) => String(v))
  );
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    setLocalSelected((selectedValues || []).map((v) => String(v)));
  }, [selectedKey, selectedValues]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleToggle = (value) => {
    const v = String(value);
    setLocalSelected((prev) => {
      const next = prev.map((x) => String(x));
      if (next.includes(v)) {
        return next.filter((x) => x !== v);
      }
      return [...next, v];
    });
  };

  const handleApply = () => {
    onApply(columnKey, localSelected);
    onClose();
  };

  const handleReset = () => {
    setLocalSelected([]);
    setSearchText('');
    onApply(columnKey, []);
    onClose();
  };

  const filteredOptions = options.filter((opt) =>
    String(opt.label || '').toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div ref={dropdownRef} className="smt__filter-dropdown">
      <div className="smt__filter-dropdown-header">Фильтр: {title || columnKey}</div>
      {emptyOption && (
        <div className="smt__filter-dropdown-empty-option">
          <label className="smt__filter-dropdown-item">
            <input
              type="checkbox"
              checked={localSelected.map((x) => String(x)).includes(String(emptyOption.value))}
              onChange={() => handleToggle(emptyOption.value)}
            />
            <span className="smt__filter-dropdown-label">{emptyOption.text}</span>
          </label>
        </div>
      )}
      <div className="smt__filter-dropdown-search">
        <input
          type="text"
          className="smt__filter-dropdown-search-input"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Поиск..."
          aria-label={`Поиск: ${title || columnKey}`}
          autoFocus
        />
      </div>
      <div className="smt__filter-dropdown-list">
        {filteredOptions.map((opt) => {
          const optValue = opt?.value != null ? String(opt.value) : String(opt?.label ?? '');
          return (
            <label key={optValue} className="smt__filter-dropdown-item">
              <input
                type="checkbox"
                checked={localSelected.map((x) => String(x)).includes(optValue)}
                onChange={() => handleToggle(optValue)}
              />
              <span className="smt__filter-dropdown-label">{opt.label}</span>
              {opt.count !== undefined && (
                <span className="smt__filter-dropdown-count">({opt.count})</span>
              )}
            </label>
          );
        })}
        {filteredOptions.length === 0 && (
          <div className="smt__filter-dropdown-empty">Нет опций</div>
        )}
      </div>
      <div className="smt__filter-dropdown-actions">
        <button type="button" className="smt__filter-dropdown-btn smt__filter-dropdown-btn--reset" onClick={handleReset}>
          Сбросить
        </button>
        <button type="button" className="smt__filter-dropdown-btn smt__filter-dropdown-btn--apply" onClick={handleApply}>
          Применить
        </button>
      </div>
    </div>
  );
};


// ==================== SEARCHABLE FILTER DROPDOWN ====================

const SearchableFilterDropdown = ({
  columnKey,
  title,
  options = [],
  selectedValues = [],
  onApply,
  onClose,
}) => {
  const selectedKey = useMemo(() => serializeFilterSelection(selectedValues), [selectedValues]);
  const [localSelected, setLocalSelected] = useState(() =>
    (selectedValues || []).map((v) => String(v))
  );
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalSelected((selectedValues || []).map((v) => String(v)));
  }, [selectedKey, selectedValues]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  // Автофокус на поле ввода при открытии
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleToggle = (value) => {
    const v = String(value);
    setLocalSelected((prev) => {
      const next = prev.map((x) => String(x));
      if (next.includes(v)) {
        return next.filter((x) => x !== v);
      }
      return [...next, v];
    });
  };

  const handleApply = () => {
    // Если есть текст в поиске и он не совпадает ни с одним выбранным — добавляем как значение фильтра
    const finalValues = [...localSelected];
    const trimmed = searchText.trim();
    if (trimmed && !finalValues.includes(trimmed)) {
      finalValues.push(trimmed);
    }
    onApply(columnKey, finalValues);
    onClose();
  };

  const handleReset = () => {
    setLocalSelected([]);
    setSearchText('');
    onApply(columnKey, []);
    onClose();
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Фильтруем опции по введённому тексту (label/value могут прийти нестрокой с API)
  const filteredOptions = options.filter((opt) => {
    const lab = String(opt?.label ?? opt?.value ?? '').toLowerCase();
    return lab.includes(searchText.toLowerCase());
  });

  return (
    <div ref={dropdownRef} className="smt__filter-dropdown">
      <div className="smt__filter-dropdown-header">Фильтр: {title || columnKey}</div>
      <div className="smt__filter-dropdown-search">
        <input
          ref={inputRef}
          type="text"
          className="smt__filter-dropdown-search-input"
          value={searchText}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          placeholder={columnKey === 'comment' ? 'Текст в комментарии...' : 'Введите номер...'}
          aria-label={columnKey === 'comment' ? 'Поиск по комментарию' : 'Поиск по номеру'}
        />
      </div>
      <div className="smt__filter-dropdown-list">
        {filteredOptions.map((opt) => {
          const optValue = opt?.value != null ? String(opt.value) : String(opt?.label ?? '');
          const optLabel = opt?.label != null ? String(opt.label) : optValue;
          return (
            <label key={optValue} className="smt__filter-dropdown-item">
              <input
                type="checkbox"
              checked={localSelected.map((x) => String(x)).includes(optValue)}
              onChange={() => handleToggle(optValue)}
              />
              <span className="smt__filter-dropdown-label">{optLabel}</span>
              {opt.count !== undefined && (
                <span className="smt__filter-dropdown-count">({opt.count})</span>
              )}
            </label>
          );
        })}
        {filteredOptions.length === 0 && searchText && (
          <div className="smt__filter-dropdown-empty">
            Нет совпадений — будет применён ввод
          </div>
        )}
        {filteredOptions.length === 0 && !searchText && (
          <div className="smt__filter-dropdown-empty">
            {columnKey === 'comment'
              ? 'Нет уникальных комментариев в выборке API. Введите текст выше и нажмите «Применить» — поиск по всей таблице.'
              : 'Нет опций'}
          </div>
        )}
      </div>
      <div className="smt__filter-dropdown-actions">
        <button type="button" className="smt__filter-dropdown-btn smt__filter-dropdown-btn--reset" onClick={handleReset}>
          Сбросить
        </button>
        <button type="button" className="smt__filter-dropdown-btn smt__filter-dropdown-btn--apply" onClick={handleApply}>
          Применить
        </button>
      </div>
    </div>
  );
};

// ==================== DATE RANGE FILTER DROPDOWN ====================

/**
 * DateRangeFilterDropdown — фильтр по диапазону дат
 * Два поля: «От» и «До» (type="date")
 * Применить / Сбросить
 */
// Конфигурация цветов срочности оплаты
const URGENCY_COLORS = [
  { key: 'red', label: 'Срочно (≤1 день)', color: '#F33F4E' },
  { key: 'blue', label: 'Скоро (1–2 день)', color: '#5283F4' },
  { key: 'black', label: 'Близко (1–3 день)', color: '#383838' },
];

const DateRangeFilterDropdown = ({
  columnKey,
  title,
  selectedValues = [],
  onApply,
  onClose,
}) => {
  // selectedValues = ['from:YYYY-MM-DD', 'to:YYYY-MM-DD', 'color:red', 'color:blue', ...] или []
  const parseRange = (vals) => {
    let from = '';
    let to = '';
    const colors = [];
    (vals || []).forEach((v) => {
      if (v.startsWith('from:')) from = v.slice(5);
      if (v.startsWith('to:')) to = v.slice(3);
      if (v.startsWith('color:')) colors.push(v.slice(6));
    });
    return { from, to, colors };
  };

  const initial = parseRange(selectedValues);
  const [dateFrom, setDateFrom] = useState(initial.from);
  const [dateTo, setDateTo] = useState(initial.to);
  const [selectedColors, setSelectedColors] = useState(initial.colors);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleColorToggle = (colorKey) => {
    setSelectedColors((prev) =>
      prev.includes(colorKey)
        ? prev.filter((c) => c !== colorKey)
        : [...prev, colorKey]
    );
  };

  const handleApply = () => {
    const values = [];
    if (dateFrom) values.push(`from:${dateFrom}`);
    if (dateTo) values.push(`to:${dateTo}`);
    selectedColors.forEach((c) => values.push(`color:${c}`));
    onApply(columnKey, values);
    onClose();
  };

  const handleReset = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedColors([]);
    onApply(columnKey, []);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div ref={dropdownRef} className="smt__filter-dropdown smt__filter-dropdown--daterange">
      <div className="smt__filter-dropdown-header">Фильтр: {title || columnKey}</div>
      <div className="smt__daterange-body">
        <label className="smt__daterange-field">
          <span className="smt__daterange-label">От</span>
          <input
            type="date"
            className="smt__daterange-input"
            value={dateFrom}
            onClick={(e) => { try { e.target.showPicker(); } catch (_) { /* fallback */ } }}
            onChange={(e) => setDateFrom(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Дата от"
          />
        </label>
        <label className="smt__daterange-field">
          <span className="smt__daterange-label">До</span>
          <input
            type="date"
            className="smt__daterange-input"
            value={dateTo}
            onClick={(e) => { try { e.target.showPicker(); } catch (_) { /* fallback */ } }}
            onChange={(e) => setDateTo(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Дата до"
          />
        </label>
      </div>

      {/* Фильтры по цвету срочности — только для столбца «Следующий платёж» */}
      {columnKey === 'nextPayment' && (
        <div className="smt__urgency-section">
          <div className="smt__urgency-header">Срочность оплаты</div>
          <div className="smt__urgency-list">
            {URGENCY_COLORS.map((uc) => (
              <label key={uc.key} className="smt__urgency-item" tabIndex={0}>
                <input
                  type="checkbox"
                  checked={selectedColors.includes(uc.key)}
                  onChange={() => handleColorToggle(uc.key)}
                />
                <span
                  className="smt__urgency-dot"
                  style={{ background: uc.color }}
                />
                <span className="smt__urgency-label">{uc.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="smt__filter-dropdown-actions">
        <button type="button" className="smt__filter-dropdown-btn smt__filter-dropdown-btn--reset" onClick={handleReset}>
          Сбросить
        </button>
        <button type="button" className="smt__filter-dropdown-btn smt__filter-dropdown-btn--apply" onClick={handleApply}>
          Применить
        </button>
      </div>
    </div>
  );
};

// ==================== COLUMN HEADER ====================

const ColumnHeader = ({
  label,
  columnKey,
  sortable = false,
  filterable = false,
  searchable = false,
  dateRange = false,
  sortConfig,
  onSort,
  onFilter,
  filterOptions = [],
  activeFilters = {},
  onFilterApply,
  emptyOption = null,
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isActive = sortConfig?.key === columnKey;
  const direction = isActive ? sortConfig.direction : 'asc';
  const hasActiveFilter = activeFilters[columnKey]?.length > 0;

  const handleSortClick = () => {
    if (sortable && onSort) {
      onSort(columnKey);
    }
  };

  const handleFilterClick = (e) => {
    e.stopPropagation();
    if (filterable) {
      setIsFilterOpen((prev) => !prev);
    }
  };

  const handleFilterApply = (key, values) => {
    if (onFilterApply) {
      onFilterApply(key, values);
    }
  };

  const handleFilterClose = () => {
    setIsFilterOpen(false);
  };

  const getFilterComponent = () => {
    if (dateRange) return DateRangeFilterDropdown;
    if (searchable) return SearchableFilterDropdown;
    return ColumnFilterDropdown;
  };

  const FilterDropdownComponent = getFilterComponent();

  return (
    <div className="smt__header-content">
      <span className="smt__header-label">{label}</span>
      {sortable && (
        <button
          type="button"
          className="smt__header-btn"
          onClick={handleSortClick}
          aria-label={`Сортировать по ${label}`}
          tabIndex={0}
        >
          <SortIcon active={isActive} direction={direction} />
        </button>
      )}
      {filterable && (
        <div
          className={`smt__filter-wrapper${columnKey === 'freezeUntil' ? ' smt__filter-wrapper--align-end' : ''}`}
        >
          <button
            type="button"
            className={`smt__header-btn ${hasActiveFilter ? 'smt__header-btn--active' : ''}`}
            onClick={handleFilterClick}
            aria-label={`Фильтр по ${label}`}
            tabIndex={0}
          >
            <FilterIcon active={hasActiveFilter} />
          </button>
          {isFilterOpen && (
            <FilterDropdownComponent
              columnKey={columnKey}
              title={label}
              options={filterOptions}
              selectedValues={activeFilters[columnKey] ?? EMPTY_TABLE_FILTER_SELECTION}
              onApply={handleFilterApply}
              onClose={handleFilterClose}
              emptyOption={emptyOption}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const SimTable = ({
  data = [],
  onSort,
  onFilter,
  onRowSelect,
  onBulkSelect,
  onAllSelect,
  onActionClick,
  onRowClick,
  onCellEdit,
  onMarkPaid,
  editOptions = {},
  selectedRows = [],
  activeRowId = null,
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 20,
  onPageChange,
  onItemsPerPageChange,
  columnFilterOptions = {},
  resetFiltersToken = 0,
  operatorTemplates = [],
}) => {
  const LONG_PRESS_DURATION_MS = 1500;

  const personalCabinetUrlByOperator = useMemo(() => {
    const map = new Map();
    (operatorTemplates || []).forEach((t) => {
      const nameKey = String(t?.name || '').trim().toLowerCase();
      const url = String(t?.personal_cabinet_url || '').trim();
      if (nameKey && url) {
        map.set(nameKey, url);
      }
    });
    return map;
  }, [operatorTemplates]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [activeFilters, setActiveFilters] = useState({});

  useEffect(() => {
    setActiveFilters({});
  }, [resetFiltersToken]);

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Action menu state
  const [actionMenuRowId, setActionMenuRowId] = useState(null);
  const [isActionMenuOpenUp, setIsActionMenuOpenUp] = useState(false);
  const actionMenuRef = useRef(null);
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  // Close action menu on click outside
  useEffect(() => {
    if (!actionMenuRowId) return;
    const handleClickOutside = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setActionMenuRowId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [actionMenuRowId]);

  const handleActionMenuToggle = useCallback((rowId, e) => {
    e.stopPropagation();
    const isOpening = actionMenuRowId !== rowId;
    if (isOpening) {
      const buttonRect = e.currentTarget.getBoundingClientRect();
      const estimatedMenuHeight = 128;
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const shouldOpenUp = spaceBelow < estimatedMenuHeight && buttonRect.top > estimatedMenuHeight;
      setIsActionMenuOpenUp(shouldOpenUp);
    }
    setActionMenuRowId((prev) => (prev === rowId ? null : rowId));
  }, [actionMenuRowId]);

  const handleActionMenuSelect = useCallback((row, action) => {
    setActionMenuRowId(null);
    if (onActionClick) {
      onActionClick(row, action);
    }
  }, [onActionClick]);

  const openCellEditor = useCallback((row, columnKey) => {
    if (!EDITABLE_COLUMNS[columnKey]) return;
    const value = getEditValue(row, columnKey);
    setEditingCell({ rowId: row.id, columnKey });
    setEditValue(value);
  }, []);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleCellLongPressStart = useCallback((row, columnKey, event) => {
    if (!EDITABLE_COLUMNS[columnKey]) return;
    if (event?.button !== undefined && event.button !== 0) return;
    if (event?.target?.closest('button, input, select, a, .smt__inline-editor, .smt__checkbox-btn')) return;

    clearLongPressTimer();
    longPressTriggeredRef.current = false;

    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      openCellEditor(row, columnKey);
    }, LONG_PRESS_DURATION_MS);
  }, [LONG_PRESS_DURATION_MS, clearLongPressTimer, openCellEditor]);

  const getLongPressHandlers = useCallback((row, columnKey) => ({
    onMouseDown: (event) => handleCellLongPressStart(row, columnKey, event),
    onMouseUp: clearLongPressTimer,
    onMouseLeave: clearLongPressTimer,
    onTouchStart: (event) => handleCellLongPressStart(row, columnKey, event),
    onTouchEnd: clearLongPressTimer,
    onTouchCancel: clearLongPressTimer,
  }), [clearLongPressTimer, handleCellLongPressStart]);

  // Сохранение отредактированного значения
  const handleEditSave = useCallback((rowId, columnKey, newValue) => {
    setEditingCell(null);
    setEditValue('');

    const row = data.find((r) => r.id === rowId);
    if (!row) return;

    const originalValue = getEditValue(row, columnKey);
    if (String(newValue) === String(originalValue)) return;

    if (onCellEdit) {
      onCellEdit(rowId, columnKey, newValue);
    }
  }, [data, onCellEdit]);

  // Отмена редактирования
  const handleEditCancel = useCallback(() => {
    setEditingCell(null);
    setEditValue('');
  }, []);

  useEffect(() => () => clearLongPressTimer(), [clearLongPressTimer]);

  // Проверка, редактируется ли конкретная ячейка
  const isCellEditing = (rowId, colKey) =>
    editingCell?.rowId === rowId && editingCell?.columnKey === colKey;

  // Дополнительные CSS-классы для редактируемых ячеек
  const editCellClass = (rowId, colKey) => {
    if (!EDITABLE_COLUMNS[colKey]) return '';
    return ` smt__cell--editable${isCellEditing(rowId, colKey) ? ' smt__cell--editing' : ''}`;
  };

  // Рендер инлайн-редактора или исходного контента
  const renderEditor = (row, colKey, content) => {
    if (!isCellEditing(row.id, colKey)) return content;
    const config = EDITABLE_COLUMNS[colKey];

    let options = editOptions[colKey] || [];

    // Для modemNumber: берём опции модемов и фильтруем по серверу (подключению) текущей строки
    if (colKey === 'modemNumber') {
      const modemOpts = editOptions['modem'] || [];
      options = row.server
        ? modemOpts.filter((opt) => opt.connectionName === row.server)
        : modemOpts;
    }

    return (
      <InlineEditor
        key={`${row.id}-${colKey}`}
        type={config.type}
        value={editValue}
        options={options}
        onSave={(val) => handleEditSave(row.id, colKey, val)}
        onCancel={handleEditCancel}
      />
    );
  };

  const handleSort = useCallback((columnKey) => {
    setSortConfig((prev) => {
      const newDirection = prev.key === columnKey && prev.direction === 'asc' ? 'desc' : 'asc';
      const newConfig = { key: columnKey, direction: newDirection };
      if (onSort) {
        onSort(newConfig);
      }
      return newConfig;
    });
  }, [onSort]);

  // Получить уникальные значения из данных для фильтра
  const getFilterOptions = useCallback((columnKey) => {
    // Комментарии — только из API (вся таблица), не из текущей страницы
    if (columnKey === 'comment') {
      const opts = columnFilterOptions?.comment;
      return Array.isArray(opts) ? opts : [];
    }

    if (columnKey === 'imei' || columnKey === 'mac') {
      const opts = columnFilterOptions?.[columnKey];
      return Array.isArray(opts) ? opts : [];
    }

    if (columnKey === 'modemNumber' && columnFilterOptions?.[columnKey]?.length > 0) {
      const selectedServers = activeFilters.server || [];
      if (selectedServers.length === 0) {
        return columnFilterOptions[columnKey];
      }
      return columnFilterOptions[columnKey].filter((option) =>
        selectedServers.includes(option.serverName)
      );
    }

    if (columnKey === 'phoneNumber' && columnFilterOptions?.[columnKey]?.length > 0) {
      let options = columnFilterOptions[columnKey];
      const selectedServers = activeFilters.server || [];
      const selectedModems = activeFilters.modemNumber || [];

      if (selectedServers.length > 0) {
        options = options.filter((option) =>
          !option.serverName || selectedServers.includes(option.serverName)
        );
      }

      if (selectedModems.length > 0) {
        options = options.filter((option) =>
          Array.isArray(option.modemUiValues)
          && option.modemUiValues.some((value) => selectedModems.includes(value))
        );
      }

      return options;
    }

    // Если есть внешние опции, используем их
    if (columnFilterOptions?.[columnKey]?.length > 0) {
      return columnFilterOptions[columnKey];
    }

    // Иначе формируем из данных
    const fieldMap = {
      modem: 'modemStatus',
      sim: 'simStatus',
      geo: 'geo',
      server: 'server',
      modemNumber: 'modemNumber',
      phoneNumber: 'phoneNumber',
      operator: 'operator',
      amount: 'amount',
      registeredBy: 'registeredBy',
      registeredOn: 'registeredOn',
    };

    const field = fieldMap[columnKey];
    if (!field) return [];

    const counts = {};
    data.forEach((row) => {
      const value = row[field];
      if (value !== undefined && value !== null && value !== '') {
        counts[value] = (counts[value] || 0) + 1;
      }
    });

    return Object.entries(counts).map(([value, count]) => ({
      value,
      label: String(value),
      count,
    }));
  }, [data, columnFilterOptions, activeFilters.server, activeFilters.modemNumber]);

  const getEmptyFilterOption = useCallback((columnKey) => {
    if (columnKey === 'imei') {
      const count = Number(columnFilterOptions?.imeiEmptyCount) || 0;
      return { text: `Без данных (${count})`, value: IDENTITY_EMPTY_FILTER };
    }
    if (columnKey === 'mac') {
      const count = Number(columnFilterOptions?.macEmptyCount) || 0;
      return { text: `Без данных (${count})`, value: IDENTITY_EMPTY_FILTER };
    }
    return null;
  }, [columnFilterOptions?.imeiEmptyCount, columnFilterOptions?.macEmptyCount]);

  // Применение фильтра по столбцу
  const handleFilterApply = useCallback((columnKey, values) => {
    setActiveFilters((prev) => {
      const next = {
        ...prev,
        [columnKey]: values,
      };

      if (columnKey === 'server') {
        const allowedServers = new Set(values || []);

        if ((next.modemNumber || []).length > 0) {
          const modemOptions = columnFilterOptions?.modemNumber || [];
          next.modemNumber = next.modemNumber.filter((modemValue) =>
            modemOptions.some((option) =>
              option.value === modemValue
              && (allowedServers.size === 0 || allowedServers.has(option.serverName))
            )
          );
          const modemFilterValue = next.modemNumber.length > 0 ? next.modemNumber.join(',') : null;
          if (onFilter) {
            onFilter('modemNumber', modemFilterValue);
          }
        }

        if ((next.phoneNumber || []).length > 0) {
          const phoneOptions = columnFilterOptions?.phoneNumber || [];
          next.phoneNumber = next.phoneNumber.filter((phoneValue) =>
            phoneOptions.some((option) =>
              option.value === phoneValue
              && (allowedServers.size === 0 || allowedServers.has(option.serverName))
              && (
                (next.modemNumber || []).length === 0
                || (
                  Array.isArray(option.modemUiValues)
                  && option.modemUiValues.some((value) => next.modemNumber.includes(value))
                )
              )
            )
          );
          const phoneFilterValue = next.phoneNumber.length > 0 ? next.phoneNumber.join(',') : null;
          if (onFilter) {
            onFilter('phoneNumber', phoneFilterValue);
          }
        }
      }

      if (columnKey === 'modemNumber' && (next.phoneNumber || []).length > 0) {
        const selectedModems = new Set(values || []);
        const phoneOptions = columnFilterOptions?.phoneNumber || [];
        next.phoneNumber = next.phoneNumber.filter((phoneValue) =>
          phoneOptions.some((option) =>
            option.value === phoneValue
            && (
              selectedModems.size === 0
              || (
                Array.isArray(option.modemUiValues)
                && option.modemUiValues.some((value) => selectedModems.has(value))
              )
            )
          )
        );
        const phoneFilterValue = next.phoneNumber.length > 0 ? next.phoneNumber.join(',') : null;
        if (onFilter) {
          onFilter('phoneNumber', phoneFilterValue);
        }
      }

      return next;
    });

    if (onFilter) {
      let filterValue = null;
      if (values.length > 0) {
        // Комментарии могут содержать запятые — несколько подстрок OR на бэкенде через |||
        filterValue = columnKey === 'comment' ? values.join('|||') : values.join(',');
      }
      onFilter(columnKey, filterValue);
    }
  }, [onFilter, columnFilterOptions]);

  const handleFilter = useCallback((columnKey) => {
    if (onFilter) {
      onFilter(columnKey);
    }
  }, [onFilter]);

  const lastClickedIndexRef = useRef(null);

  const handleRowSelect = (rowId, event) => {
    const currentIndex = data.findIndex((r) => r.id === rowId);

    if (event?.shiftKey && lastClickedIndexRef.current !== null && lastClickedIndexRef.current !== currentIndex && currentIndex >= 0) {
      const start = Math.min(lastClickedIndexRef.current, currentIndex);
      const end = Math.max(lastClickedIndexRef.current, currentIndex);
      const rangeIds = data.slice(start, end + 1).map((r) => r.id);
      const shouldSelect = !selectedRows.includes(rowId);

      if (onBulkSelect) {
        onBulkSelect(rangeIds, shouldSelect);
      } else if (onRowSelect) {
        rangeIds.forEach((id) => {
          const isSelected = selectedRows.includes(id);
          if (shouldSelect && !isSelected) onRowSelect(id);
          if (!shouldSelect && isSelected) onRowSelect(id);
        });
      }
    } else {
      if (onRowSelect) {
        onRowSelect(rowId);
      }
    }

    lastClickedIndexRef.current = currentIndex >= 0 ? currentIndex : null;
  };

  const handleAllSelect = () => {
    if (onAllSelect) {
      onAllSelect();
    }
  };

  // Дней до даты следующего платежа (отрицательное — просрочка); невалидная дата → null
  const getDaysUntilNextPayment = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('.');
    if (parts.length !== 3) return null;
    const date = new Date(parts[2], parts[1] - 1, parts[0]);
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return Math.round((date - now) / (1000 * 60 * 60 * 24));
  };

  // Уровень срочности платежа: red (≤сегодня), blue (1–2-й день), black (3-й день), null
  const getDateUrgency = (dateStr) => {
    const diffDays = getDaysUntilNextPayment(dateStr);
    if (diffDays === null) return null;
    if (diffDays <= 0) return 'red';
    if (diffDays === 1 || diffDays === 2) return 'blue';
    if (diffDays === 3) return 'black';
    return null;
  };

  /** Кнопка «Оплатить»: за 3 дня до срока, в день срока и после (просрочка) */
  const isPayButtonDue = (dateStr) => {
    const diffDays = getDaysUntilNextPayment(dateStr);
    return diffDays !== null && diffDays <= 3;
  };

  /** В последние 3 календарных дня до окончания заморозки (и при просрочке) — красная обводка строки; как кнопка «Оплатить» */
  const isFreezeEndingSoon = (dateStr) => {
    const diffDays = getDaysUntilNextPayment(dateStr);
    return diffDays !== null && diffDays <= 3;
  };

  // Вычисление класса строки по статусу модема/SIM (по макету node 436-50935)
  const getRowStatusClass = useCallback((row) => {
    const modemLower = (row.modemStatus || '').toLowerCase();
    const simLower = (row.simStatus || '').toLowerCase();

    if (simLower.includes('заморожен') || simLower === 'frozen') {
      return 'smt__row--frozen';
    }

    // Резерв СПб/Мск -> зеленый тон 2
    if (simLower.includes('резерв')) {
      return 'smt__row--reserve';
    }
    // Свободен -> зеленый тон 1
    if (simLower.includes('свободен') || simLower === 'free') {
      return 'smt__row--free';
    }
    // Проверить -> темный фон
    if (simLower.includes('проверить') || simLower === 'check') {
      return 'smt__row--warning';
    }
    // Неизвестно -> ярко-красный фон
    if (modemLower.includes('неизвестно') || simLower.includes('неизвестно') || simLower === 'unknown') {
      return 'smt__row--unknown';
    }
    // Недоступен или Заменить -> danger (мягкий красный фон)
    if (modemLower.includes('отключен') || modemLower.includes('недоступен') || simLower.includes('заменить')) {
      return 'smt__row--danger';
    }
    return '';
  }, []);

  const allSelected = data.length > 0 && selectedRows.length === data.length;
  const someSelected = selectedRows.length > 0 && selectedRows.length < data.length;

  // Столбцы по макету
  const columns = [
    { key: 'modem', label: 'Доступность', sortable: true, filterable: true, width: 140 },
    { key: 'sim', label: 'SIM', sortable: true, filterable: true, width: 140 },
    { key: 'geo', label: 'ГЕО', sortable: true, filterable: true, width: 90 },
    { key: 'server', label: 'Сервер', sortable: true, filterable: true, width: 160 },
    { key: 'modemNumber', label: '№ Модема', sortable: true, filterable: true, width: 180 },
    { key: 'phoneNumber', label: 'Номер', sortable: true, filterable: true, searchable: true, width: 140 },
    { key: 'operator', label: 'Оператор', sortable: true, filterable: true, width: 140 },
    { key: 'amount', label: 'Сумма, ₽', sortable: true, filterable: true, width: 120 },
    // { key: 'lastPayment', label: 'Последн...', sortable: true, filterable: true, dateRange: true, width: 120 },
    { key: 'nextPayment', label: 'Следую...', sortable: true, filterable: true, dateRange: true, width: 120 },
    { key: 'paid', label: '', sortable: false, filterable: false, width: 90 },
    { key: 'registeredBy', label: 'Личный кабинет', sortable: true, filterable: true, width: 'flex' },
    { key: 'registeredOn', label: 'Оформлена', sortable: true, filterable: true, width: 150 },
    { key: 'comment', label: 'Комментарий', sortable: false, filterable: true, searchable: true, width: 140 },
    { key: 'freezeUntil', label: 'Заморозка до', sortable: true, filterable: true, dateRange: true, width: 120 },
    { key: 'imei', label: 'IMEI', sortable: false, filterable: true, width: 150 },
    { key: 'mac', label: 'MAC', sortable: false, filterable: true, width: 150 },
    { key: 'modemImei', label: 'IMEI Модема', sortable: false, filterable: false, width: 150 },
    { key: 'modemMac', label: 'MAC Модема', sortable: false, filterable: false, width: 150 },
  ];

  const _startItem = data.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0; // eslint-disable-line no-unused-vars
  const _endItem = Math.min(currentPage * itemsPerPage, totalItems); // eslint-disable-line no-unused-vars

  return (
    <div className="smt">
      {/* Table */}
      <div className="smt__table-wrap">
        <table className="smt__table">
          <thead>
            <tr className="smt__row smt__row--header">
              {/* Checkbox header */}
              <th className="smt__cell smt__cell--header smt__cell--checkbox">
                <button
                  type="button"
                  className="smt__checkbox-btn"
                  onClick={handleAllSelect}
                  aria-label="Выбрать все"
                  tabIndex={0}
                >
                  <CheckboxIcon checked={allSelected} indeterminate={someSelected} />
                </button>
              </th>
              {/* Columns */}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`smt__cell smt__cell--header smt__cell--${col.key}`}
                  style={col.width !== 'flex' ? { width: col.width, minWidth: col.width } : {}}
                >
                  <ColumnHeader
                    label={col.label}
                    columnKey={col.key}
                    sortable={col.sortable}
                    filterable={col.filterable}
                    searchable={col.searchable || false}
                    dateRange={col.dateRange || false}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    onFilter={handleFilter}
                    filterOptions={getFilterOptions(col.key)}
                    activeFilters={activeFilters}
                    onFilterApply={handleFilterApply}
                    emptyOption={getEmptyFilterOption(col.key)}
                  />
                </th>
              ))}
              {/* Actions header */}
              <th className="smt__cell smt__cell--header smt__cell--actions">
                <MenuDotsIcon />
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => {
              const isSelected = selectedRows.includes(row.id);
              const isActive = activeRowId != null && row.id === activeRowId;
              const rowToneClass = isSelected
                ? 'smt__row--selected'
                : (isActive ? 'smt__row--active' : getRowStatusClass(row));
              return (
                <tr
                  key={row.id || index}
                  className={`smt__row ${rowToneClass}${isFreezeEndingSoon(row.freezeUntil) ? ' smt__row--freeze-soon' : ''}`}
                  onClick={(e) => {
                    if (longPressTriggeredRef.current) {
                      longPressTriggeredRef.current = false;
                      return;
                    }
                    if (onRowClick && !e.target.closest('button, input, select, a, .smt__checkbox-btn, .smt__cell--actions, .smt__inline-editor')) {
                      onRowClick(row);
                    }
                  }}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {/* Checkbox */}
                  <td className="smt__cell smt__cell--checkbox">
                    <button
                      type="button"
                      className="smt__checkbox-btn"
                      onClick={(e) => handleRowSelect(row.id, e)}
                      aria-label={`Выбрать строку ${index + 1}`}
                      tabIndex={0}
                    >
                      <CheckboxIcon checked={isSelected} />
                    </button>
                  </td>
                  {/* Модем */}
                  <td
                    className={`smt__cell smt__cell--modem${editCellClass(row.id, 'modem')}`}
                    {...getLongPressHandlers(row, 'modem')}
                  >
                    {renderEditor(row, 'modem', <StatusBadge status={row.modemStatus} />)}
                  </td>
                  {/* SIM */}
                  <td
                    className={`smt__cell smt__cell--sim${editCellClass(row.id, 'sim')}`}
                    {...getLongPressHandlers(row, 'sim')}
                  >
                    {renderEditor(row, 'sim', (
                      <SimStatusBadge
                        status={row.simStatus}
                        modemLongUnavailable={Boolean(row.modemLongUnavailable72h)}
                      />
                    ))}
                  </td>
                  {/* Заморозка (Да/Нет) */}
                  {/* ГЕО */}
                  <td
                    className={`smt__cell smt__cell--geo smt__cell--muted${editCellClass(row.id, 'geo')}`}
                    {...getLongPressHandlers(row, 'geo')}
                  >
                    {renderEditor(row, 'geo', row.geo || '—')}
                  </td>
                  {/* Сервер */}
                  <td
                    className={`smt__cell smt__cell--server smt__cell--muted${row.serverDisabled ? ' smt__cell--server-disabled' : ''}${editCellClass(row.id, 'server')}`}
                    {...getLongPressHandlers(row, 'server')}
                  >
                    {renderEditor(row, 'server', row.server || '—')}
                  </td>
                  {/* Номер модема */}
                  <td
                    className={`smt__cell smt__cell--modemNumber smt__cell--muted${editCellClass(row.id, 'modemNumber')}`}
                    {...getLongPressHandlers(row, 'modemNumber')}
                  >
                    {renderEditor(row, 'modemNumber', (() => {
                      const modemNumberText = String(row.modemNumber || '');
                      const modemNumberMatch = modemNumberText.match(/^(№\s*\d+)\s*(.*)$/);

                      if (!modemNumberMatch) {
                        return modemNumberText || '—';
                      }

                      const [, modemPrefix, modemName] = modemNumberMatch;
                      return (
                        <span className="smt__modem-number">
                          <span className="smt__modem-number-prefix">{modemPrefix}</span>
                          {modemName ? <span> {modemName}</span> : null}
                        </span>
                      );
                    })())}
                  </td>
                  {/* Номер телефона */}
                  <td
                    className={`smt__cell smt__cell--phoneNumber smt__cell--muted${editCellClass(row.id, 'phoneNumber')}`}
                    {...getLongPressHandlers(row, 'phoneNumber')}
                  >
                    {renderEditor(row, 'phoneNumber', row.phoneNumber)}
                  </td>
                  {/* Оператор */}
                  <td
                    className={`smt__cell smt__cell--operator${editCellClass(row.id, 'operator')}`}
                    {...getLongPressHandlers(row, 'operator')}
                  >
                    {renderEditor(row, 'operator', <OperatorBadge operator={row.operator} />)}
                  </td>
                  {/* Сумма, ₽ */}
                  <td
                    className={`smt__cell smt__cell--amount smt__cell--muted${editCellClass(row.id, 'amount')}`}
                    {...getLongPressHandlers(row, 'amount')}
                  >
                    {renderEditor(row, 'amount', row.amount)}
                  </td>
                  {/* Последний платеж — скрыт */}
                  {/* Следующий платеж */}
                  <td
                    className={`smt__cell smt__cell--nextPayment ${getDateUrgency(row.nextPayment) ? `smt__cell--urgency-${getDateUrgency(row.nextPayment)}` : 'smt__cell--muted'}${editCellClass(row.id, 'nextPayment')}`}
                    {...getLongPressHandlers(row, 'nextPayment')}
                  >
                    {renderEditor(row, 'nextPayment', row.nextPayment)}
                  </td>
                  {/* Оплачено */}
                  <td className="smt__cell smt__cell--paid">
                    {(() => {
                      const payDue = isPayButtonDue(row.nextPayment);

                      return (
                        <button
                          type="button"
                          className={`smt__paid-btn${payDue ? ' smt__paid-btn--overdue' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            const paymentUrl = buildAlfaMobilePaymentUrl(row.phoneNumber, row.amount);
                            if (paymentUrl) {
                              window.open(paymentUrl, '_blank', 'noopener,noreferrer');
                            }
                            if (onMarkPaid) onMarkPaid(row);
                          }}
                          aria-label="Оплатить"
                          tabIndex={0}
                        >
                          Оплатить
                        </button>
                      );
                    })()}
                  </td>
                  {/* Личный кабинет */}
                  <td
                    className={`smt__cell smt__cell--registeredBy${editCellClass(row.id, 'registeredBy')}`}
                    {...getLongPressHandlers(row, 'registeredBy')}
                  >
                    {renderEditor(row, 'registeredBy', (
                      <div className="smt__registered-content">
                        <span className="smt__registered-text">{row.registeredBy}</span>
                        {row.personalAccount && (() => {
                          const opKey = String(row.personalAccount.operator || '').trim().toLowerCase();
                          const urlTemplate = personalCabinetUrlByOperator.get(opKey) || '';
                          let lkHref = buildPersonalCabinetHrefFromTemplate(urlTemplate, {
                            phone: row.personalAccount.phone_number,
                            phone_number: row.phoneNumber,
                          });
                          if (
                            !lkHref
                            && row.personalAccount.phone_number
                            && (opKey.includes('мегафон') || opKey.includes('megafon'))
                          ) {
                            lkHref = buildPersonalCabinetHrefFromTemplate(
                              'https://lk.megafon.ru/login/#phone={phone}',
                              {
                                phone: row.personalAccount.phone_number,
                                phone_number: row.phoneNumber,
                              }
                            );
                          }
                          const title = `${row.personalAccount.operator} · ${row.personalAccount.phone_number || '—'}`;

                          if (lkHref) {
                            return (
                              <a
                                href={lkHref}
                                className="smt__pa-badge"
                                title={title}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                tabIndex={0}
                                aria-label={`Личный кабинет ${row.personalAccount.operator || ''}`}
                              >
                                ЛК
                              </a>
                            );
                          }

                          return (
                            <span className="smt__pa-badge" title={title}>
                              ЛК
                            </span>
                          );
                        })()}
                        {!row.personalAccount && row.accountLink && (
                          <a
                            href={row.accountLink}
                            className="smt__account-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            tabIndex={0}
                          >
                            ЛК
                          </a>
                        )}
                      </div>
                    ))}
                  </td>
                  <td
                    className={`smt__cell smt__cell--registeredOn smt__cell--muted${editCellClass(row.id, 'registeredOn')}`}
                    {...getLongPressHandlers(row, 'registeredOn')}
                  >
                    {renderEditor(row, 'registeredOn', row.registeredOn)}
                  </td>
                  {/* Комментарий */}
                  <td
                    className={`smt__cell smt__cell--comment smt__cell--muted${editCellClass(row.id, 'comment')}`}
                    {...getLongPressHandlers(row, 'comment')}
                  >
                    {renderEditor(row, 'comment', <span className="smt__comment-text" title={row.comment}>{row.comment}</span>)}
                  </td>
                  {/* Заморозка до */}
                  <td
                    className={`smt__cell smt__cell--freezeUntil smt__cell--muted${editCellClass(row.id, 'freezeUntil')}`}
                    {...getLongPressHandlers(row, 'freezeUntil')}
                  >
                    {renderEditor(row, 'freezeUntil', row.freezeUntil || '—')}
                  </td>
                  <td className="smt__cell smt__cell--imei smt__cell--muted">
                    <span className="smt__comment-text" title={row.imei}>{row.imei || '—'}</span>
                  </td>
                  <td className="smt__cell smt__cell--mac smt__cell--muted">
                    <span className="smt__comment-text" title={row.mac}>{row.mac || '—'}</span>
                  </td>
                  <td className="smt__cell smt__cell--modemImei smt__cell--muted">
                    <span className="smt__comment-text" title={row.modemImei}>{row.modemImei || '—'}</span>
                  </td>
                  <td className="smt__cell smt__cell--modemMac smt__cell--muted">
                    <span className="smt__comment-text" title={row.modemMac}>{row.modemMac || '—'}</span>
                  </td>
                  {/* Действия — выпадающее меню */}
                  <td className="smt__cell smt__cell--actions">
                    <div className="smt__action-wrapper" ref={actionMenuRowId === row.id ? actionMenuRef : undefined}>
                      <button
                        type="button"
                        className={`smt__action-btn${actionMenuRowId === row.id ? ' smt__action-btn--open' : ''}`}
                        onClick={(e) => handleActionMenuToggle(row.id, e)}
                        aria-label="Действия"
                        tabIndex={0}
                      >
                        <MenuDotsIcon />
                      </button>
                      {actionMenuRowId === row.id && (
                        <div className={`smt__action-menu${isActionMenuOpenUp ? ' smt__action-menu--up' : ''}`}>
                          <button
                            type="button"
                            className="smt__action-menu-item"
                            onClick={() => handleActionMenuSelect(row, 'checkProxy')}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M12 2v3"/><path d="M12 19v3"/><path d="M4.93 4.93l2.12 2.12"/><path d="M16.95 16.95l2.12 2.12"/><path d="M2 12h3"/><path d="M19 12h3"/><path d="M4.93 19.07l2.12-2.12"/><path d="M16.95 7.05l2.12-2.12"/></svg>
                            Проверить прокси
                          </button>
                          <button
                            type="button"
                            className="smt__action-menu-item"
                            onClick={() => handleActionMenuSelect(row, 'edit')}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>
                            Редактировать
                          </button>
                          <button
                            type="button"
                            className="smt__action-menu-item smt__action-menu-item--danger"
                            onClick={() => handleActionMenuSelect(row, 'delete')}
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                            Удалить
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {data.length === 0 && (
              <tr className="smt__row smt__row--empty">
                <td colSpan={columns.length + 2} className="smt__cell smt__cell--empty">
                  Нет данных
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <CustomPagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        perPage={itemsPerPage}
        perPageOptions={[10, 20, 50, 100]}
        onPageChange={onPageChange}
        onPerPageChange={onItemsPerPageChange}
      />
    </div>
  );
};

export default SimTable;
